import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Grid from '@mui/material/Grid2';
import { Box, Button } from '@mui/material';

import validationSchema from 'src/utils/validationSchema';

import { useTranslate } from 'src/locales';

import { Form, Field } from 'src/components/hook-form';

import { useChannelContext } from '../../hooks';

function LiveChatStep1() {
  const { t } = useTranslate('translation');

  const { onChangeStep, state: channelState, handleLiveChatChannelDtls } = useChannelContext();

  const { websiteDomin, websiteName, widgetColor, welcomeTitle, welcomeTagline } =
    channelState.LiveChatDeatil || {
      websiteName: '',
      websiteDomin: '',
      widgetColor: '',
      welcomeTitle: '',
      welcomeTagline: '',
    };

  const [color, setColor] = useState<string>(widgetColor || '#000000');

  type LiveChatField = {
    websiteName: string;
    websiteDomin: string;
    widgetColor: string;
    welcomeTitle: string;
    welcomeTagline: string;
  };

  const fldList = [
    { name: 'websiteName', required: true },
    { name: 'websiteDomin', required: true },
    { name: 'widgetColor', required: false },
    { name: 'welcomeTitle', required: true },
    { name: 'welcomeTagline', required: true },
  ];

  const defaultValues: LiveChatField = {
    websiteName: '',
    websiteDomin: '',
    widgetColor: '',
    welcomeTitle: '',
    welcomeTagline: '',
  };

  const LiveChatSchema = validationSchema(fldList, t);

  const methods = useForm<LiveChatField>({
    mode: 'all',
    resolver: zodResolver(LiveChatSchema),
    defaultValues,
    values: { websiteDomin, websiteName, widgetColor, welcomeTitle, welcomeTagline },
  });

  const { handleSubmit, watch, setValue } = methods;

  const [watchedWebsitename, watchedWebsiteDomin, watchedWelcomeTagline, watchedWelcomeTitle] =
    watch(['websiteName', 'websiteDomin', 'welcomeTagline', 'welcomeTitle']);
  // console.log('🚀 ~ LiveChatStep1 ~ watchedWebsitename:', watchedWebsitename);

  const onSubmit = handleSubmit(async (data: any) => {
    // handleLiveChatChannelDtls({ websiteDomin: '', websiteName: '', widgetColor: '', welcomeTitle: '',welcomeTagline:'' });
    handleLiveChatChannelDtls(data);
    // { websiteDomin: '', websiteName: '', widgetColor: '', welcomeTitle: '',welcomeTagline:'' }
  });

  return (
    <Form methods={methods} onSubmit={onSubmit} style={{ height: '100%' }}>
      <Box sx={{ height: '90%', display: 'flex', flexDirection: 'column' }}>
        <Box>
          <Grid container spacing={3} justifyContent="space-between">
            <Field.Text
              name="websiteName"
              label={t('WEB_NAME')}
              slotProps={{ inputLabel: { shrink: true } }}
              placeholder="Enter your website name"
            />
            <Field.Text
              name="websiteDomin"
              label={t('WEB_DOMAIN')}
              slotProps={{ inputLabel: { shrink: true } }}
              placeholder="Enter your website domain"
            />
            <Field.Text
              name="widgetColor"
              label={t('WIDGET_COLOR')}
              sx={{ width: '30%' }}
              slotProps={{ inputLabel: { shrink: true } }}
              type="color"
              value={color}
              onChange={(e) => {
                const newColor = e.target.value || '#000000';
                setColor(newColor);
                setValue('widgetColor', newColor);
              }}
            />
            <Field.Text
              name="welcomeTitle"
              label={t('WELL_HEADING')}
              slotProps={{ inputLabel: { shrink: true } }}
              placeholder="Hey there! 👋"
            />
            <Field.Text
              name="welcomeTagline"
              label={t('WELL_TAGLINE')}
              slotProps={{ inputLabel: { shrink: true } }}
              placeholder="Welcome! How can we assist you today?"
            />
          </Grid>
        </Box>

        <Box sx={{ flexGrow: 1 }} />
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end',
            alignItems: 'center',
            my: 2,
          }}
        >
          <Box>
            <Button variant="outlined" color="inherit" onClick={() => onChangeStep('back')}>
              {t('PREVIOUS')}
            </Button>
          </Box>
          <Box>
            <Button
              disabled={
                !watchedWebsitename ||
                !watchedWebsiteDomin ||
                !watchedWelcomeTitle ||
                !watchedWelcomeTagline
              }
              size="small"
              variant="contained"
              type="submit"
              onClick={() => onChangeStep('next')}
            >
              {t('NEXT')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Form>
  );
}

export default LiveChatStep1;
